import { useState, useEffect, useCallback } from 'react';
import { useAccount, useNetwork } from 'wagmi';
import { ethers } from 'ethers';
import { createDORContract } from '@/contracts/DORContract';
import { getNetworkConfig } from '@/config/networks';

export interface DORTokenInfo {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
}

export interface DORStats {
  totalSupply: string;
  totalLocked: string;
  price: string;
}

export interface LockInfo {
  lockedAmount: string;
  unlockTime: Date;
  isLocked: boolean;
}

export const useDORContract = () => {
  const { address, isConnected } = useAccount();
  const { chain } = useNetwork();
  const [contract, setContract] = useState<any>(null);
  const [tokenInfo, setTokenInfo] = useState<DORTokenInfo | null>(null);
  const [userBalance, setUserBalance] = useState<string>('0');
  const [userCirculatingBalance, setUserCirculatingBalance] = useState<string>('0');
  const [userLockInfo, setUserLockInfo] = useState<LockInfo | null>(null);
  const [inviteReward, setInviteReward] = useState<string>('0');
  const [stats, setStats] = useState<DORStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 初始化合约
  useEffect(() => {
    const initContract = async () => {
      if (chain?.id && isConnected && window.ethereum) {
        try {
          const network = getNetworkConfig(chain.id);
          const provider = new ethers.BrowserProvider(window.ethereum as any);
          const signer = await provider.getSigner();
          const dorContract = createDORContract(chain.id, signer);
          setContract(dorContract);
        } catch (err) {
          console.error('初始化合约失败:', err);
          setError('合约初始化失败');
        }
      }
    };

    initContract();
  }, [chain?.id, isConnected]);

  // 获取代币信息
  const fetchTokenInfo = useCallback(async () => {
    if (!contract) return;

    try {
      setLoading(true);
      const info = await contract.getTokenInfo();
      setTokenInfo(info);
    } catch (err) {
      console.error('获取代币信息失败:', err);
      setError('获取代币信息失败');
    } finally {
      setLoading(false);
    }
  }, [contract]);

  // 获取用户余额
  const fetchUserBalance = useCallback(async () => {
    if (!contract || !address) return;

    try {
      setLoading(true);
      const balance = await contract.getBalance(address);
      setUserBalance(balance);
    } catch (err) {
      console.error('获取用户余额失败:', err);
      setError('获取用户余额失败');
    } finally {
      setLoading(false);
    }
  }, [contract, address]);

  // 获取用户流通余额
  const fetchUserCirculatingBalance = useCallback(async () => {
    if (!contract || !address) return;

    try {
      setLoading(true);
      const balance = await contract.getCirculatingBalance(address);
      setUserCirculatingBalance(balance);
    } catch (err) {
      console.error('获取用户流通余额失败:', err);
      setError('获取用户流通余额失败');
    } finally {
      setLoading(false);
    }
  }, [contract, address]);

  // 获取用户锁定信息
  const fetchUserLockInfo = useCallback(async () => {
    if (!contract || !address) return;

    try {
      setLoading(true);
      const lockInfo = await contract.getLockInfo(address);
      setUserLockInfo(lockInfo);
    } catch (err) {
      console.error('获取锁定信息失败:', err);
      setError('获取锁定信息失败');
    } finally {
      setLoading(false);
    }
  }, [contract, address]);

  // 获取邀请奖励
  const fetchInviteReward = useCallback(async () => {
    if (!contract || !address) return;

    try {
      setLoading(true);
      const reward = await contract.getInviteReward(address);
      setInviteReward(reward);
    } catch (err) {
      console.error('获取邀请奖励失败:', err);
      setError('获取邀请奖励失败');
    } finally {
      setLoading(false);
    }
  }, [contract, address]);

  // 获取统计信息
  const fetchStats = useCallback(async () => {
    if (!contract) return;

    try {
      setLoading(true);
      const statsData = await contract.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('获取统计信息失败:', err);
      setError('获取统计信息失败');
    } finally {
      setLoading(false);
    }
  }, [contract]);

  // 设置邀请人
  const setInviter = useCallback(async (inviterAddress: string) => {
    if (!contract) throw new Error('合约未初始化');

    try {
      setLoading(true);
      const tx = await contract.setInviter(inviterAddress);
      await tx.wait();
      return tx;
    } catch (err) {
      console.error('设置邀请人失败:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [contract]);

  // 充值
  const recharge = useCallback(async (usdtAmount: string) => {
    if (!contract) throw new Error('合约未初始化');

    try {
      setLoading(true);
      const tx = await contract.recharge(usdtAmount);
      await tx.wait();
      return tx;
    } catch (err) {
      console.error('充值失败:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [contract]);

  // 带邀请人的充值
  const rechargeWithInviter = useCallback(async (usdtAmount: string, inviterAddress: string) => {
    if (!contract) throw new Error('合约未初始化');

    try {
      setLoading(true);
      const tx = await contract.rechargeWithInviter(usdtAmount, inviterAddress);
      await tx.wait();
      return tx;
    } catch (err) {
      console.error('带邀请人的充值失败:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [contract]);

  // 赎回
  const redeem = useCallback(async (dorAmount: string) => {
    if (!contract) throw new Error('合约未初始化');

    try {
      setLoading(true);
      const tx = await contract.redeem(dorAmount);
      await tx.wait();
      return tx;
    } catch (err) {
      console.error('赎回失败:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [contract]);

  // 锁定DOR
  const lockDOR = useCallback(async (amount: string, lockTime: number) => {
    if (!contract) throw new Error('合约未初始化');

    try {
      setLoading(true);
      const tx = await contract.lockDOR(amount, lockTime);
      await tx.wait();
      return tx;
    } catch (err) {
      console.error('锁定DOR失败:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [contract]);

  // 解锁DOR
  const unlockDOR = useCallback(async () => {
    if (!contract) throw new Error('合约未初始化');

    try {
      setLoading(true);
      const tx = await contract.unlockDOR();
      await tx.wait();
      return tx;
    } catch (err) {
      console.error('解锁DOR失败:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [contract]);

  // 提取邀请奖励
  const claimInviteReward = useCallback(async () => {
    if (!contract) throw new Error('合约未初始化');

    try {
      setLoading(true);
      const tx = await contract.claimInviteReward();
      await tx.wait();
      return tx;
    } catch (err) {
      console.error('提取邀请奖励失败:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [contract]);

  // USDT 授权
  const approveUSDT = useCallback(async (amount: string) => {
    if (!contract) throw new Error('合约未初始化');
    if (!chain?.id) throw new Error('网络未识别');
    const network = getNetworkConfig(chain.id);
    const usdtAddress = network.contracts.usdtToken;
    const dorAddress = network.contracts.dorToken;
    try {
      setLoading(true);
      const tx = await contract.approveUSDT(usdtAddress, dorAddress, amount);
      await tx.wait();
      return tx;
    } catch (err) {
      console.error('USDT 授权失败:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [contract, chain?.id]);

  // 刷新所有数据
  const refreshAll = useCallback(async () => {
    if (!contract) return;

    try {
      setLoading(true);
      // 顺序调用而不是并行调用，减少同时请求数量
      await fetchTokenInfo();
      await fetchUserBalance();
      await fetchUserCirculatingBalance();
      await fetchUserLockInfo();
      await fetchInviteReward();
      await fetchStats();
    } catch (err) {
      console.error('刷新数据失败:', err);
      setError('刷新数据失败');
    } finally {
      setLoading(false);
    }
  }, [contract, fetchTokenInfo, fetchUserBalance, fetchUserCirculatingBalance, fetchUserLockInfo, fetchInviteReward, fetchStats]);

  // 初始化时获取数据
  useEffect(() => {
    if (contract && address) {
      refreshAll();
    }
  }, [contract, address, refreshAll]);

  return {
    contract,
    tokenInfo,
    userBalance,
    userCirculatingBalance,
    userLockInfo,
    inviteReward,
    stats,
    loading,
    error,
    setInviter,
    recharge,
    rechargeWithInviter,
    redeem,
    lockDOR,
    unlockDOR,
    claimInviteReward,
    approveUSDT,
    refreshAll,
    fetchTokenInfo,
    fetchUserBalance,
    fetchUserCirculatingBalance,
    fetchUserLockInfo,
    fetchInviteReward,
    fetchStats,
  };
}; 